import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { CenteredSkeleton, FieldSkeleton } from "../../components/skeletons.jsx";
import { useI18n } from "../../i18n/index.js";
import { authClient } from "../../lib/auth-client.js";
import { invalidateSession } from "../../lib/session.js";
import { Brand, Button, CenteredPage, FormError, GitHubIcon, Skeleton, TextField } from "../../ui/index.js";

/** While a fresh load finds out whether someone is already signed in (and so leaves this page). */
export function SignInSkeleton() {
  return (
    <CenteredSkeleton>
      <Skeleton shape="button" width="100%" />
      <div className="dt-divider">
        <Skeleton width={24} />
      </div>
      <div className="dt-form">
        <FieldSkeleton label={<Skeleton width={48} />} />
        <FieldSkeleton label={<Skeleton width={72} />} />
        <Skeleton shape="button" width="100%" />
      </div>
    </CenteredSkeleton>
  );
}

export function SignInRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t, tNode } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = params.get("next") ?? "/";

  return (
    <CenteredPage>
      <div className="dt-centered__head">
        <Brand size="lg" />
        <div>
          <h1>{t("signIn.title")}</h1>
          <p className="dt-muted">{t("signIn.subtitle")}</p>
        </div>
      </div>
      <Button variant="secondary" icon={<GitHubIcon />} onClick={() => authClient.signIn.social({ provider: "github", callbackURL: next })}>
        {t("signIn.github")}
      </Button>
      <div className="dt-divider">{t("signIn.or")}</div>
      <form
        className="dt-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          const form = new FormData(e.currentTarget);
          const { error } = await authClient.signIn.email({ email: String(form.get("email")), password: String(form.get("password")) });
          setBusy(false);
          if (error) setError(error.message ?? t("signIn.failed"));
          else {
            // This page's middleware cached "signed out"; drop it or the next
            // navigation would bounce straight back here.
            invalidateSession();
            navigate(next.startsWith("/") ? next : "/");
          }
        }}
      >
        <TextField name="email" label={t("field.email")} type="email" placeholder={t("field.emailPlaceholder")} required autoComplete="email" />
        <TextField name="password" label={t("field.password")} type="password" required autoComplete="current-password" />
        <FormError message={error} />
        <Button type="submit" variant="primary" loading={busy}>
          {t("signIn.submit")}
        </Button>
      </form>
      <p className="dt-centered__foot">
        {tNode("signIn.noAccount", { link: <Link to={`/sign-up?next=${encodeURIComponent(next)}`}>{t("signIn.signUpLink")}</Link> })}
      </p>
    </CenteredPage>
  );
}
